import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faInfoCircle} from '@fortawesome/fontawesome-pro-solid';

import {ToolTipCell} from 'components/ToolTipCell';
import MultiSelect from 'components/MultiSelect';
import {getFieldNameKey} from './utils';
import DynamicFilter from './dynamic';
import {getSelectedFiltersFromUrl} from './url';

/** TODO: Remove legacy filters and logic like selectedStates and selectedPlanTypes */
export const CoverageFilters = ({
  states,
  planTypes,
  selectedStates,
  selectedPlanTypes,
  selectedDynamicFilters,
  coveragePositions,
  inProgress,
  settings,
  styleSheet,
  urlParameters,
  onUrlFiltersUpdate,
  onFilterChange,
}) => {
  // Track previous filter data to detect changes
  const prevFilterDataRef = useRef({
    states,
    planTypes,
    coveragePositions,
  });

  // Effect to handle URL parameter parsing when filter data loads
  useEffect(() => {
    // Check if filter data has changed (loaded)
    const prevFilterData = prevFilterDataRef.current;
    let filtersChanged = false;

    // Compare all filter data from Redux
    if (JSON.stringify(states) !== JSON.stringify(prevFilterData.states) ||
        JSON.stringify(planTypes) !== JSON.stringify(prevFilterData.planTypes) ||
        JSON.stringify(coveragePositions) !== JSON.stringify(prevFilterData.coveragePositions)) {
      filtersChanged = true;
    }

    // Only proceed if filters changed AND URL parameters exist
    if (!filtersChanged || !urlParameters || Object.keys(urlParameters).length === 0) {
      // Update ref for next comparison
      prevFilterDataRef.current = {
        states,
        planTypes,
        coveragePositions,
      };
      return;
    }

    // Check if dynamic filters are enabled
    const hasDynamicFilters = settings.filterLayout && settings.filterLayout.some((f) => f.filter === 'dynamic');

    // Check which filter types are present in URL parameters
    const urlFilterTypes = new Set();
    if (urlParameters && typeof urlParameters === 'object' && urlParameters !== null) {
      // Check for static filters in URL
      for (let filter of settings.filterLayout) {
        if (filter.filter !== 'dynamic') {
          const filterAlias = settings.urlParametersMap[filter.filter]?.alias || filter.filter;
          if (Object.prototype.hasOwnProperty.call(urlParameters, filterAlias)) {
            urlFilterTypes.add(filter.filter);
          }
        }
      }

      // Check for dynamic filters in URL
      if (hasDynamicFilters) {
        for (let filter of settings.filterLayout) {
          if (filter.filter === 'dynamic') {
            const fieldName = filter.fieldName;
            const fieldNameKey = getFieldNameKey(fieldName);
            const possibleKeys = [
              settings.urlParametersMap[fieldNameKey]?.alias || fieldNameKey,
              fieldNameKey,
              fieldName,
              settings.urlParametersMap[fieldName]?.alias || fieldName,
            ];

            if (possibleKeys.some((key) => Object.prototype.hasOwnProperty.call(urlParameters, key))) {
              urlFilterTypes.add('dynamic');
              break;
            }
          }
        }
      }
    }

    // Verify only required filter data is loaded (only for filters present in URL)
    let allFiltersLoaded = true;
    const urlParsingProps = {
      states,
      usStates: states, // getValidItemsFromUrl expects 'usStates' for state filter
      planTypes,
      settings,
    };

    // Only check static filters that are in the URL
    for (let filter of settings.filterLayout) {
      if (filter.filter !== 'dynamic' && urlFilterTypes.has(filter.filter)) {
        const filterPropString = (filter.filter === 'state')
          ? 'states'
          : (filter.filter === 'plan')
            ? 'planTypes'
            : `${filter.filter}s`;

        const filterData = urlParsingProps[filterPropString];
        if (!filterData || !Array.isArray(filterData) || filterData.length === 0) {
          allFiltersLoaded = false;
          break;
        }
      }
    }

    // If dynamic filters are in URL, ensure coveragePositions is loaded
    if (urlFilterTypes.has('dynamic') && (!coveragePositions || !Array.isArray(coveragePositions) || coveragePositions.length === 0)) {
      allFiltersLoaded = false;
    }

    // If no filters in URL, we're done
    if (urlFilterTypes.size === 0) {
      allFiltersLoaded = false;
    }

    // If all filters loaded, parse URL and update parent
    if (allFiltersLoaded && onUrlFiltersUpdate) {
      const selectedFilters = getSelectedFiltersFromUrl(
        urlParameters,
        settings.filterLayout,
        urlParsingProps,
        coveragePositions
      );

      // Only call if we have filters to apply
      if (Object.keys(selectedFilters).length > 0) {
        onUrlFiltersUpdate(selectedFilters);
      }
    }

    // Update ref for next comparison
    prevFilterDataRef.current = {
      states,
      planTypes,
      coveragePositions,
    };
  }, [states, planTypes, coveragePositions, urlParameters, settings, onUrlFiltersUpdate]);

  // Internal handleChange function that calls onFilterChange callback
  const handleFilterChange = (selected, type, singleSelect) => {
    // eslint-disable-next-line
    switch (type) {
      case 'states':
        onFilterChange({selectedStates: selected});
        break;
      case 'planTypes':
        onFilterChange({selectedPlanTypes: selected});
        break;
      default:
        break;
    }
  };

  const multiSelectFilter = (column, options, selected, type, additionalProps = {}) => {
    // Create a wrapper function that captures singleSelect from column
    const createHandleChange = (type) => (selected) => {
      handleFilterChange(selected, type, column.singleSelect);
    };
    // Normalize selected so each has valid label for MultiSelect (API may use 'name' instead of 'label')
    const normalizedSelected = (selected || []).filter(Boolean).map((s) => ({
      ...s,
      label: s.label ?? s.name ?? String(s.id ?? ''),
    }));
    return (
      <Col key={column.filter} sm={column.gridWidth} className={`offset-sm-${column.offset}`}>
        <MultiSelect
          label={additionalProps.label || column.label}
          placeholder={column.placeholder}
          options={options}
          selected={normalizedSelected}
          isFetching={inProgress}
          handleChange={createHandleChange(type)}
          selectHintOnEnter={false}
          type={type}
          {...additionalProps}
        />
      </Col>
    );
  };

  return (
    <Row as="section" aria-label="Filters" key="filterInputs">
      {settings.filterLayout.map((column) => {
        // Handle dynamic filter type using DynamicFilter component
        if (column.filter === 'dynamic' && coveragePositions) {
          const fieldName = column.fieldName;
          return (
            <DynamicFilter
              key={`dynamic-${getFieldNameKey(fieldName)}`}
              column={column}
              coveragePositions={coveragePositions}
              selectedDynamicFilters={selectedDynamicFilters}
              onFilterChange={onFilterChange}
              inProgress={inProgress}
              settings={settings}
              styleSheet={styleSheet}
            />
          );
        }

        switch (column.filter) {
          case 'state': {
            // Ensure each state has valid label for MultiSelect (API may use 'name' or 'label')
            const stateOptions = (states || []).map((s) => ({
              ...s,
              label: s.label ?? s.name ?? String(s.id ?? ''),
            }));
            return multiSelectFilter(column, stateOptions, selectedStates, 'states');
          }
          case 'plan': {
            const displayedPlanTypes = settings.displayedPlanTypes.length
              ? settings.displayedPlanTypes.map((planType) => {
                const base = {id: planType.ids.join(','), ...planType};
                // Ensure label exists for MultiSelect (API may use 'name' instead of 'label')
                base.label = base.label ?? base.name ?? String(base.id ?? '');
                return base;
              })
              : (planTypes || []).map((pt) => {
                const base = {...pt};
                base.label = base.label ?? base.name ?? String(base.id ?? '');
                return base;
              });
            // labels with hover are rendered as accessible tooltips
            // otherwise labels are just plain text
            const hasHelpHover = settings.content?.planTypeHelpHover?.length > 0;
            const planTypeDescriptionId = 'plantype-description';
            const planLabel = hasHelpHover
              ? <span>{column.label}
                <ToolTipCell
                  text={settings.content.planTypeHelpHover}
                  placeholder={(<FontAwesomeIcon icon={faInfoCircle}/>)}
                  placement={'bottom'}
                  styleSheet={styleSheet}
                  id={'plantype-tooltip'}
                  ariaLabel={'More information about plan types'}
                />
                <span id={planTypeDescriptionId} className="sr-only visually-hidden">
                  {settings.content.planTypeHelpHover}
                </span>
              </span>
              : column.label;
            return multiSelectFilter(
              column,
              displayedPlanTypes,
              selectedPlanTypes,
              'planTypes',
              {
                label: planLabel,
                hasChildren: column.hasChildren,
                dataTestId: 'plan-type-multiselect',
                ...(hasHelpHover && {
                  typeAheadProps: {inputProps: {'aria-describedby': planTypeDescriptionId}},
                }),
              }
            );
          }
          default:
            return null;
        }
      })}
    </Row>
  );
};

CoverageFilters.propTypes = {
  states: PropTypes.array,
  planTypes: PropTypes.array,
  selectedStates: PropTypes.array.isRequired,
  selectedPlanTypes: PropTypes.array.isRequired,
  selectedDynamicFilters: PropTypes.object,
  coveragePositions: PropTypes.array,
  inProgress: PropTypes.bool.isRequired,
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  urlParameters: PropTypes.object,
  onUrlFiltersUpdate: PropTypes.func,
  onFilterChange: PropTypes.func.isRequired,
};

const mapStateToProps = function (state) {
  const {dynamicCoverage} = state;

  const result = {
    states: dynamicCoverage.get('states').toJS(),
    planTypes: dynamicCoverage.get('planTypes').toJS(),
    coveragePositions: dynamicCoverage.get('coveragePositions').toJS(),
  };

  return result;
};

export default connect(mapStateToProps)(CoverageFilters);

